import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';

import Schedule from 'features/scheduleBuilder/ScheduleBuilderView/resources/Schedule';
import { selectDefaultRoleOption } from 'features/scheduleBuilder/ScheduleBuilderView/selectors/shared';

import {
  captureHydrationException,
  getSliceState,
  selectAttributeForEntity,
} from '../helpers';
import { selectJobRelationshipId } from '../jobs/slice';
import { selectRoleById } from '../roles/slice';
import { ScheduleFetchAction } from '../types';

import { SLICE_NAME } from './constants';
import {
  RoleWage,
  RoleWageRelationshipKeys,
  RoleWageSerializedEntity,
  RoleWagesState,
} from './types';

export const roleWagesAdapter = createEntityAdapter<RoleWage>();

export const initialState: RoleWagesState = {
  ...roleWagesAdapter.getInitialState(),
  status: 'idle',
  error: undefined,
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(
      Schedule.resourceActionEvent('fetch'),
      (state: RoleWagesState, action: ScheduleFetchAction) => {
        state.status = 'succeeded';
        if (action.payload?.included) {
          try {
            const roleWageEntities = (
              action?.payload?.included as RoleWageSerializedEntity[]
            ).filter(
              (entity: RoleWageSerializedEntity) => entity.type === 'roleWage'
            );

            const roleWages = roleWageEntities.map(
              (entity: RoleWageSerializedEntity) => ({
                id: entity.id,
                type: entity.type,
                wageRate: entity.attributes.wageRate,
                ...Object.fromEntries(
                  Object.entries(entity.relationships || {}).map(
                    ([k, v]: [string, any]) => [k, v.data]
                  )
                ),
              })
            );

            roleWagesAdapter.setAll(state, roleWages);
          } catch (error) {
            captureHydrationException(state, error, SLICE_NAME);
          }
        }
      }
    );
  },
});

export const {
  selectById: selectRoleWageById,
  selectIds: selectRoleWageIds,
  selectEntities: selectRoleWageEntities,
  selectAll: selectAllRoleWages,
  selectTotal: selectTotalRoleWages,
} = roleWagesAdapter.getSelectors<RootState>(
  state => getSliceState(state)?.[SLICE_NAME] || initialState
);

export function selectRoleWageAttribute<K extends keyof RoleWage>(opts: {
  state: RootState;
  id: EntityId;
  attribute: K;
}): RoleWage[K] | undefined {
  return selectAttributeForEntity({
    state: opts.state,
    id: opts.id,
    attribute: opts.attribute,
    entitySelector: selectRoleWageById,
  });
}

export const selectRoleWageRelationshipId = ({
  state,
  id,
  relationship,
}: {
  state: RootState;
  id: EntityId;
  relationship: RoleWageRelationshipKeys;
}): EntityId | undefined => {
  const roleWage = selectRoleWageById(state, id);
  if (!roleWage) {
    return undefined;
  }

  if (relationship === 'role') {
    return roleWage.role?.id;
  }

  return undefined;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const selectEmployeeRoleWages = createCachedSelector(
  [
    (state: RootState, props: { jobId: string }) =>
      selectJobRelationshipId({
        state,
        id: props.jobId,
        relationship: 'roleWages',
      }),
    (state: RootState) => state,
    (state: RootState, props: { jobId: string }) =>
      (selectDefaultRoleOption as any)(state, {
        jobId: props.jobId,
        isAdding: true,
      }),
  ],
  (roleWageIds: EntityId[], state: RootState, defaultRole: any) => {
    let roleWageIdsArray: EntityId[];

    if (Array.isArray(roleWageIds)) {
      roleWageIdsArray = roleWageIds;
    } else if (roleWageIds) {
      roleWageIdsArray = [roleWageIds];
    } else {
      roleWageIdsArray = [];
    }

    const activeRoleWageIds = roleWageIdsArray.filter(
      (roleWageId: EntityId) => {
        const roleId = selectRoleWageRelationshipId({
          state,
          id: roleWageId,
          relationship: 'role',
        });

        const role = selectRoleById(state, roleId as EntityId);
        return !!role;
      }
    );

    const roleWages = activeRoleWageIds.map((roleWageId: EntityId) => {
      const roleId = selectRoleWageRelationshipId({
        state,
        id: roleWageId,
        relationship: 'role',
      });

      const roleEntity = selectRoleById(state, roleId as string);
      const wageRate = selectRoleWageAttribute({
        state,
        id: roleWageId,
        attribute: 'wageRate',
      });

      return {
        label: roleEntity?.name,
        value: roleEntity?.name,
        wage: wageRate,
        color: roleEntity?.color,
      };
    });

    return [...roleWages, defaultRole];
  }
)((_, props: { jobId: EntityId }) => `selectEmployeeRoleWages-${props.jobId}`);

export const reducer = slice.reducer;
